import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo, useEffect, useRef } from "react";
import { Section } from "@/components/Section";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Reveal } from "@/components/Reveal";
import { OptImg } from "@/components/OptImg";
import { CATEGORIES } from "@/lib/blog";
import { formatDateTimeBR } from "@/lib/format";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { listPublishedPostsPaged, listPublishedVideos, listActiveServices } from "@/lib/content.functions";
import { extractYouTubeId } from "@/lib/internal-links";
import { SITE } from "@/lib/site";
import { seoTitle, seoDescription } from "@/lib/seo-meta";

const PAGE_SIZE = 12;

const TITLE = seoTitle("Artigos — Concurso Astor");
const DESC = seoDescription(
  "Artigos, análises e dicas sobre gestão, estratégia, finanças, marketing e vendas — publicados pelo time editorial do Concurso Astor.",
);

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "/blog" },
    ],
    links: [{ rel: "canonical", href: `${SITE.baseUrl}/blog` }],
  }),
  component: Blog,
});


function Blog() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState<string>("Todas");

  const {
    data: postsPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["public-posts-paged"],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      listPublishedPostsPaged({ data: { limit: PAGE_SIZE, offset: pageParam as number } }),
    getNextPageParam: (last, all) => {
      const loaded = all.reduce((n, p) => n + (p?.rows?.length ?? 0), 0);
      return loaded < (last?.total ?? 0) ? loaded : undefined;
    },
    staleTime: 60_000,
  });
  const dbPosts = useMemo(
    () => (postsPages?.pages ?? []).flatMap((p: any) => p?.rows ?? []),
    [postsPages],
  );

  const { data: videos = [] } = useQuery({
    queryKey: ["public-videos"],
    queryFn: () => listPublishedVideos(),
    staleTime: 60_000,
  });
  const { data: services = [] } = useQuery({
    queryKey: ["public-services"],
    queryFn: () => listActiveServices(),
    staleTime: 60_000,
  });
  const dynamicCategories = useMemo(() => {
    const fromPosts = new Set(dbPosts.map((p: any) => p.category).filter(Boolean));
    const fromServices = services.map((s: any) => s.label);
    const merged = new Set<string>([...fromServices, ...Array.from(fromPosts), ...CATEGORIES]);
    return Array.from(merged);
  }, [services, dbPosts]);

  const allPosts = useMemo(() => {
    return dbPosts.map((p: any) => ({
      slug: p.slug,
      title: p.title,
      excerpt: p.excerpt || p.description,
      category: p.category,
      cover: p.cover,
      coverImage: p.cover_image,
      date: p.published_at ?? new Date().toISOString(),
      readingTime: p.reading_time,
    }));
  }, [dbPosts]);

  const filtered = useMemo(() => allPosts.filter((p: any) => {
    const matchQ = q === "" || p.title.toLowerCase().includes(q.toLowerCase()) || (p.excerpt ?? "").toLowerCase().includes(q.toLowerCase());
    const matchC = cat === "Todas" || p.category === cat;
    return matchQ && matchC;
  }), [q, cat, allPosts]);

  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }, { rootMargin: "600px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if ((q.trim() || cat !== "Todas") && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [q, cat, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <>
      <section className="hero-bg">
        <div className="container-svg pt-12 pb-12 md:pt-20 md:pb-16">
          <Breadcrumbs items={[{ label: "Início", to: "/" }, { label: "Artigos" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="eyebrow">Todos os artigos</span>
            <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl"><span className="text-gradient">Artigos do Concurso Astor</span></h1>
            <p className="mt-4 text-muted-foreground md:text-lg">Análises e dicas sobre gestão, estratégia, finanças, marketing, vendas e produtividade — publicados semanalmente pelo time editorial.</p>
          </div>
        </div>
      </section>

      <Section>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar artigos, análises, guias..."
            aria-label="Buscar artigos"
            className="w-full rounded border border-border bg-background px-4 py-3 text-sm md:max-w-sm focus:border-primary focus:outline-none"
          />
          <div className="flex flex-wrap gap-2">
            {["Todas", ...dynamicCategories].map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`rounded border-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition ${cat === c ? "border-primary bg-primary text-primary-foreground" : "border-border bg-secondary text-foreground hover:bg-accent hover:border-primary"}`}
              >{c}</button>
            ))}
          </div>
        </div>

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p: any, idx: number) => (
            <Reveal key={p.slug} delay={(idx % 3) as 0 | 1 | 2}>
              <Link to="/blog/$slug" params={{ slug: p.slug }} className="card-glass flex flex-col">
                {p.coverImage ? (
                  <OptImg
                    src={p.coverImage}
                    alt={p.title}
                    width={640}
                    height={360}
                    widths={[320, 480, 640, 800]}
                    sizes="(max-width: 768px) 92vw, 33vw"
                    className="mb-4 h-40 w-full rounded object-cover"
                  />
                ) : (
                  <div className="mb-4 h-40 rounded bg-accent" />
                )}
                <span className="eyebrow">{p.category}</span>
                <h2 className="mt-3 text-lg font-semibold leading-snug">{p.title}</h2>
                <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{p.excerpt}</p>
                <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                  <span>{formatDateTimeBR(p.date)}</span>
                  <span>{p.readingTime}</span>
                </div>
              </Link>
            </Reveal>
          ))}
          {filtered.length === 0 && !isFetchingNextPage && (
            <p className="text-muted-foreground">Nenhum artigo encontrado.</p>
          )}
        </div>
        <div ref={sentinelRef} className="h-10" aria-hidden />
        {isFetchingNextPage && (
          <p className="mt-4 text-center text-sm text-muted-foreground">Carregando mais artigos...</p>
        )}
      </Section>

      {videos.length > 0 && (
        <Section className="bg-[color:var(--color-sidebar)]">
          <div className="max-w-3xl">
            <span className="eyebrow">Vídeo-reportagens</span>
            <h2 className="mt-3 text-3xl font-bold tracking-tight md:text-4xl">Conteúdo em vídeo</h2>
            <p className="mt-3 text-muted-foreground">Vídeos selecionados pelo time editorial para aprofundar os temas abordados na plataforma.</p>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {videos.map((v, idx) => {
              const id = extractYouTubeId(v.url);
              return (
                <Reveal key={v.id} delay={(idx % 3) as 0 | 1 | 2}>
                  <article className="card-glass flex h-full flex-col overflow-hidden p-0">
                    <div className="relative aspect-video w-full bg-black">
                      {id ? (
                        <iframe
                          src={`https://www.youtube.com/embed/${id}`}
                          title={v.title}
                          loading="lazy"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="absolute inset-0 h-full w-full"
                        />
                      ) : (
                        <a href={v.url} target="_blank" rel="noopener" className="absolute inset-0 grid place-items-center text-sm text-primary">
                          Abrir vídeo →
                        </a>
                      )}
                    </div>
                    <div className="flex flex-1 flex-col p-5">
                      <h3 className="text-base font-semibold leading-snug">{v.title}</h3>
                      {v.description && <p className="mt-2 text-sm text-muted-foreground">{v.description}</p>}
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </Section>
      )}
    </>
  );
}
